import { App, Skeleton } from "antd"
import { useCallbackRef } from "../../../hooks/use-callback-ref.ts"
import React, { useEffect, useMemo, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"
import { useLocation, useNavigate } from "react-router-dom"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useLeadStatusChange } from "../../../hooks/lead/use-lead-status-change.tsx"
import { useRefetchOnFocus } from "../../../hooks/use-refetch-on-focus.ts"
import { type RootState } from "../../../store.ts"
import { ArchiveLeadModal } from "../../Leads/components/ArchiveLeadModal.tsx"
import { confirmLeadRestore } from "../../Leads/helpers/confirm-lead-restore.ts"
import {
  fetchLeadsFilterOptions,
  reactivateLead
} from "../../Leads/redux/leads.thunks.ts"
import { LeadLoadFailure } from "../components/LeadLoadFailure.tsx"
import { leadDisplayName } from "../helpers/lead-display-name.ts"
import { leadSetProp } from "../redux/lead.slice.ts"
import {
  fetchLead,
  toggleLeadFavorite,
  updateLead
} from "../redux/lead.thunks.ts"
import { type Lead, type LeadChecklistItem } from "../types/lead.types.ts"
import { LeadWorkspace } from "./components/LeadWorkspace.tsx"
import {
  type LeadPatchOptions,
  type LeadWorkspacePatch
} from "./lead-workspace-patch.ts"
import { useLeadMissingFields } from "./missing/use-lead-missing-fields.ts"

const LeadWorkspacePage = ({
  lead,
  fetching,
  notFound,
  saving,
  filterOptions,
  leadPermissions,
  getLeadById,
  updateLeadById,
  toggleFavorite,
  restoreLead,
  setProp,
  getLeadsFilterOptions
}: ConnectedProps<typeof connector>) => {
  const id = useParams()?.id as string
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const [archiveOpen, setArchiveOpen] = useState(false)
  const { pendingStatus, applyRejectedStage, clearPendingStage } =
    useLeadMissingFields()

  const { confirmStatusChange, changeStatus, statusChangeDialogs } =
    useLeadStatusChange({
      refetchCallback: () => {
        if (id) getLeadById(id)
        // The transition the deep link was carrying has landed, so its marks have
        // nothing left to point at.
        clearPendingStage()
      },
      // A rejection answered while this lead is open rewrites the params the
      // marks derive from, so a retry that fails on a different field re-marks
      // the cards instead of leaving the first set standing.
      onMissingFields: ({ targetStatus, fields, leadId }) => {
        if (leadId && leadId !== id) return
        applyRejectedStage(targetStatus ?? null, fields ?? [])
      },
      // Declining the PM declines the move, so every later save must stop
      // retrying it.
      onProjectManagerPromptCancel: clearPendingStage,
      onBackwardTransitionCancel: clearPendingStage
    })

  // The deep-linked stage is retried without a second confirmation: the user
  // already confirmed this move, and the rejection is what sent them here.
  const retryPendingStage = () => {
    if (pendingStatus != null) {
      changeStatus(id, pendingStatus)
    }
  }

  // The workspace must not render a lead another page left in the store: its
  // cards would offer to save the wrong record.
  const current = lead?.id === id ? lead : null

  const getLeadByIdLatest = useCallbackRef(getLeadById)
  useEffect(() => {
    if (id) {
      getLeadByIdLatest(id)
    }
  }, [id, getLeadByIdLatest])

  // A Proposal/Contract Update-Lead sync writes straight to the backend from
  // a document editor's own route — nothing in this SPA's state ever tells
  // an already-open Lead tab a sync happened elsewhere (a second tab, or
  // this same tab returning from a background/occluded state), the same gap
  // `useRefetchOnFocus` already closes for an RSVP answered from a separate
  // email tab (see the "open Quick View" decision). `getLeadById` is the
  // same silent-enough refresh the `activity_completed` case above already
  // relies on — `mutatedLead` stays truthy through the refetch, so `fetching`
  // never re-triggers the full-page skeleton for what is, most of the time,
  // an unchanged lead.
  useRefetchOnFocus(() => {
    if (id) getLeadById(id)
  }, !!id)

  useEffect(() => {
    generateDocumentTitle(
      current
        ? leadDisplayName(current, t("leadWorkspace.header.untitled"))
        : "Lead Workspace"
    )
  }, [current, t])

  const getLeadsFilterOptionsLatest = useCallbackRef(getLeadsFilterOptions)
  useEffect(() => {
    getLeadsFilterOptionsLatest()
  }, [getLeadsFilterOptionsLatest])

  const mutatedLead: Lead | null = useMemo(() => {
    if (!current) return null
    if (!current?.source) return current

    return {
      ...current,
      source: filterOptions?.source?.find((s) => s?.value === current?.source)
        ?.label
    }
  }, [current, filterOptions?.source])

  if (fetching && !mutatedLead)
    return <Skeleton.Node active className={"size-full"} />

  if (!mutatedLead) {
    return (
      <LeadLoadFailure
        notFound={notFound}
        retrying={fetching}
        onRetry={() => getLeadById(id)}
      />
    )
  }

  const handleLeadSelect = (id: string, action: string) => {
    switch (action) {
      case "archive":
        return setArchiveOpen(true)
      // The Quick Details popover completed the linked follow-up event
      // directly against the Calendar API, so the header needs a reread —
      // the same reload every other lead patch runs.
      case "activity_completed":
        return getLeadById(id)
      case "restore":
        return confirmLeadRestore({
          modal,
          t,
          lead: mutatedLead,
          onConfirm: async () => {
            await restoreLead(id).unwrap()
            getLeadById(id)
          }
        })
      case "back_to_list": {
        // `location.key` is react-router's own per-entry id; it is the
        // literal string "default" only for the entry the app was loaded on.
        // `window.history.length` was wrong for this: it counts the whole tab
        // session, including pages from before the SPA, so a lead opened by
        // pasting its URL into a tab that had another site loaded sent Back
        // off to that other site.
        if (location.key !== "default") {
          return navigate(-1)
        } else {
          return navigate(RoutesPaths.lead.list)
        }
      }
      default:
        return alert(`${action} - ${id}`)
    }
  }

  const handleLeadChecklistUpdate = async (
    lead_checklist: LeadChecklistItem[]
  ) => {
    try {
      await updateLeadById({ id, lead_checklist }).unwrap()
      setProp({ key: "saveResult", value: null })
    } catch {
      // The thunk already surfaced the failure; the checklist keeps the lead's
      // stored items rather than rejecting into an unhandled promise.
    }
  }

  const handleStageChange = (targetStatus: number) =>
    confirmStatusChange({ id: mutatedLead.id }, targetStatus)

  const handleToggleFavorite = () => toggleFavorite(mutatedLead.id)

  const handleLeadPatch = async (
    patch: LeadWorkspacePatch,
    options?: LeadPatchOptions
  ) => {
    try {
      await updateLeadById({ id, ...patch }).unwrap()
      setProp({ key: "saveResult", value: null })
      retryPendingStage()
      return true
    } catch (rejection) {
      options?.onRejected?.(rejection)
      return false
    }
  }

  return (
    <>
      {statusChangeDialogs}
      <LeadWorkspace
        lead={mutatedLead}
        canEdit={!!leadPermissions?.leads?.lead_edit}
        canAssignOwner={!!leadPermissions?.features?.lead_assign_sales_manager}
        saving={saving}
        filterOptions={filterOptions}
        leadPermissions={leadPermissions}
        onLeadSelect={handleLeadSelect}
        onStageChange={handleStageChange}
        onStageRetry={retryPendingStage}
        onToggleFavorite={handleToggleFavorite}
        onPatch={handleLeadPatch}
        onChecklistUpdate={handleLeadChecklistUpdate}
      />
      <ArchiveLeadModal
        open={archiveOpen}
        leadId={mutatedLead.id}
        leadName={mutatedLead.lead_name}
        onCancel={() => setArchiveOpen(false)}
        onArchived={() => {
          setArchiveOpen(false)
          getLeadById(mutatedLead.id)
        }}
      />
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.lead.fetching || state.leads.fetchingFilterOptions,
  notFound: state.lead.notFound,
  saving: state.lead.saveFetching,
  lead: state.lead.lead,
  filterOptions: state.leads.filterOptions,
  leadPermissions: state.auth?.userInfo?.permissions?.codes?.leads
})
const mapDispatchToProps = {
  getLeadsFilterOptions: fetchLeadsFilterOptions,
  getLeadById: fetchLead,
  toggleFavorite: toggleLeadFavorite,
  updateLeadById: updateLead,
  restoreLead: reactivateLead,
  setProp: leadSetProp
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(LeadWorkspacePage)
